import { useSyncExternalStore } from 'react'

type Theme = 'dark' | 'light' | 'system'
type ResolvedTheme = 'dark' | 'light'

const STORAGE_KEY = 'cogo-studio-theme'

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'light'
  return (localStorage.getItem(STORAGE_KEY) as Theme) ?? 'light'
}

function resolve(theme: Theme): ResolvedTheme {
  return theme === 'system' ? getSystemTheme() : theme
}

function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement
  if (resolved === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

// --- External store for useSyncExternalStore ---
let listeners: Array<() => void> = []
function subscribe(cb: () => void) {
  listeners.push(cb)
  return () => {
    listeners = listeners.filter((l) => l !== cb)
  }
}
function emitChange() {
  for (const l of listeners) l()
}
function getSnapshot(): Theme {
  return getStoredTheme()
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, () => 'light' as Theme)
  const resolvedTheme = resolve(theme)

  function setTheme(next: Theme) {
    localStorage.setItem(STORAGE_KEY, next)
    applyTheme(resolve(next))
    emitChange()
  }

  return { theme, resolvedTheme, setTheme } as const
}

// Initialize on module load
if (typeof window !== 'undefined') {
  applyTheme(resolve(getStoredTheme()))

  // Listen for system theme changes when theme is 'system'
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (getStoredTheme() === 'system') {
      applyTheme(getSystemTheme())
      emitChange()
    }
  })
}
